import { getActiveTraceHttpRequestHeaders } from '@atlaskit/react-ufo/experience-trace-id-context';

import { fetchBase } from '@atlassian/pillar/runtime/fetch';

import { fg } from '@confluence/feature-gating';
import { expVal } from '@confluence/feature-experiments';
import { appendHeader, cfetch, networkTracker, shouldUseGetMethod } from '@confluence/network';
import { WaterfallMeasures } from '@confluence/action-measures/entry-points/WaterfallMeasures.ts';
import { SSRMeasures } from '@confluence/action-measures/entry-points/SSRMeasures.ts';
import { getSSRRenderInputs } from '@confluence/ssr-utilities/entry-points/ssrRenderInputs.ts';
import { getAtlAttributionHeader } from '@confluence/query-preloader-tools/entry-points/getAtlAttributionHeader.ts';
import { getQueryName } from '@confluence/query-preloader-tools/entry-points/getQueryName.ts';
import { trackInflightRequest } from '@confluence/query-preloader-boundary/entry-points/inflightRequests.ts';
import { getQueryBoundary } from '@confluence/query-preloader-boundary/entry-points/queryBoundaryStorage.ts';
import { getAsyncLocalStorage } from '@confluence/async-local-storage/entry-points/getAsyncLocalStorage.ts';
import { SLOW_QUERY_GUARD_STORAGE_NAME } from '@confluence/async-local-storage/entry-points/storageNames.ts';
import { EXTERNAL_SHARE } from '@confluence/named-routes/entry-points/routes.ts';

import {
	createExperienceAwareFetch,
	isExperienceAwareFetchAlwaysEnabled,
} from './createExperienceAwareFetch.ts';
import { getBrowserTabId } from './getBrowserTabId.ts';

const experienceAwareFetch = createExperienceAwareFetch();

export const AGG_SLOW_URI = '/gateway/api/graphql/slow';

// appends the operation names as query params, and
// includes them as headers as well
interface CreateWrappedFetchOptions {
	isForRelay?: boolean;
}

export type RelayWrappedFetchOptions = {
	shouldConsiderGetMethod?: boolean;
	operationKind?: 'query' | 'mutation' | 'subscription';
};

export type WrappedFetchRequestInit = RequestInit & {
	confluenceRelayFetchOptions?: RelayWrappedFetchOptions;
};

const appendVariablesToUrl = (url: string, variables: { [key: string]: any }) =>
	`${url}${url.includes('?') ? '&' : '?'}variables=${encodeURIComponent(
		JSON.stringify(variables),
	)}`;

const appendBatchPath = (url: string) => (url.endsWith('/batch') ? url : `${url}/batch`);
const sanitizeMarkingName = (name?: string) => name?.replace(/[^\-.\w]/g, '_');

const isExternalSharePage = (): boolean => {
	// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
	if (process.env.REACT_SSR) {
		const { uri } = getSSRRenderInputs();
		return Boolean(uri && EXTERNAL_SHARE.match(new URL(uri, 'https://confluence.local').pathname));
	}

	return Boolean(EXTERNAL_SHARE.match(window.location.pathname ?? ''));
};

export const createWrappedFetch = ({ isForRelay = false }: CreateWrappedFetchOptions = {}) => {
	return (uri: string, options: WrappedFetchRequestInit): Promise<Response> => {
		const payload: Array<{
			operationName: string;
			query: string;
			variables: { [key: string]: any };
		}> = [].concat(typeof options.body === 'string' ? JSON.parse(options.body) : []); // concat normalizes into an array

		const operationNames = payload.map(({ operationName, variables }) =>
			getQueryName(operationName, variables),
		);

		// Track individual operations for frontend
		// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
		if (!process.env.REACT_SSR && typeof window !== 'undefined') {
			networkTracker.trackGraphQLOperations(operationNames);
		}

		// pass as headers for rate-limiting compatibility
		const operationNamesList = operationNames.join(',');
		if (!options.headers) {
			options.headers = {};
		}

		if (
			isExternalSharePage() &&
			expVal('confluence_external_share_edge_fallback', 'isEnabled', false)
		) {
			appendHeader(options.headers, 'X-Edge-Fallback-To-Unidentified', 'true');
		}

		// AGG batch queries require /batch to be added to the uri and an additional header
		if (uri.includes('gateway')) {
			appendHeader(options.headers, 'X-ExperimentalApi', 'confluence-agg-beta');
			if (payload.length === 1) {
				options.body = JSON.stringify(payload[0]);
			} else if (payload.length > 1) {
				appendHeader(options.headers, 'X-ExperimentalApi', 'BatchOperations');
				if (!isForRelay) {
					uri = appendBatchPath(uri);
				}
			}
		}

		appendHeader(options.headers, 'X-APOLLO-OPERATION-NAME', operationNamesList);

		const atlAttributionHeader = getAtlAttributionHeader();
		appendHeader(options.headers, 'atl-attribution', atlAttributionHeader['atl-attribution']);

		// These headers are explicitly forwarded to cc-graphql in cc-frontend-ssr, but we need to apply them here for Bifrost requests to cc-graphql.
		// We forward the same headers for AGG and cc-graphql requests
		const { aggHeaders } = getSSRRenderInputs();
		if (aggHeaders) {
			Object.entries(aggHeaders).forEach(([header, val]) => {
				if (val) {
					appendHeader(options.headers!, header, val);
				}
			});
		}

		// Note: this may overrwrite a header value from above in SSR context, but this ensures
		// it's still applied in SPA context
		if (window['GLOBAL_AGG_REGION_STEERING_HINT']) {
			appendHeader(
				options.headers,
				'Atl-Edge-Region-Hint',
				window['GLOBAL_AGG_REGION_STEERING_HINT']!,
			);
		}

		options.referrerPolicy = options.referrerPolicy || 'same-origin';

		// Tesseract instruments the outgoing http requests automatically, no need to pass tracing headers when running in SSR.
		// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
		if (!process.env.REACT_SSR) {
			// add experience tracing headers

			const tracingHeaders = getActiveTraceHttpRequestHeaders('' /* url, not used */);
			options.headers = Object.assign(options.headers, tracingHeaders);

			if (expVal('cc_use_browser_tab_id', 'isEnabled', false)) {
				appendHeader(options.headers, 'X-ATL-Confluence-Origin-Tab', getBrowserTabId());
			}
		}

		const relayFetchOptions = options.confluenceRelayFetchOptions;
		const shouldConsiderRelayGetMethod =
			isForRelay &&
			// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
			!process.env.REACT_SSR &&
			relayFetchOptions?.shouldConsiderGetMethod === true &&
			relayFetchOptions.operationKind === 'query' &&
			payload.length === 1 &&
			uri.includes('/pq/') &&
			!fg('confluence_relay_pq_get_killswitch');
		let shouldUseGet = false;
		let fallbackReason: string | undefined;

		if (shouldConsiderRelayGetMethod) {
			const { isGetRequest, reason } = shouldUseGetMethod({
				url: uri,
				variables: payload[0].variables ?? {},
				headers: options.headers,
				payload,
			});
			shouldUseGet = isGetRequest;
			fallbackReason = reason;
		}

		// At this point the query has lost the structure and formatted back to string
		const executeFetch = () => {
			const finalOptions: WrappedFetchRequestInit = { ...options };
			delete finalOptions.confluenceRelayFetchOptions;
			let finalUri = uri;

			if (shouldUseGet) {
				finalOptions.method = 'GET';
				finalUri = appendVariablesToUrl(finalUri, payload[0].variables ?? {});
				delete finalOptions.body;
			} else if (fallbackReason) {
				appendHeader(
					finalOptions.headers as Record<string, string>,
					'X-Request-Fallback-To-Post-Reason',
					fallbackReason,
				);
			}

			if (process.env.BIFROST_TEMPLATE_BUILD === 'true') {
				/**
				 * Request route through AP when x-ssr-asap-audience is provided.
				 */
				const headers = (finalOptions.headers || {}) as Record<string, string>;

				// AGG requests don't set the x-ssr-asap-audience/service-name headers because the
				// AGG host is handled specially.
				let baseUrl = isForRelay ? finalUri : AGG_SLOW_URI;
				if (!isForRelay && baseUrl.includes('gateway') && payload.length > 1) {
					baseUrl = appendBatchPath(baseUrl);
				}

				const urlWithQuery = isForRelay ? baseUrl : `${baseUrl}?q=${operationNamesList}`;

				// Track SSR measures for Bifrost - fetchBase bypasses cfetch where
				// fetchCountIncr is normally called
				SSRMeasures.fetchCountIncr(1, { isUsingRelay: isForRelay });

				if (!headers['Atl-Edge-Region-Hint']) {
					// eslint-disable-next-line no-console
					console.warn(
						`Gateway GraphQL request missing Atl-Edge-Region-Hint header: ${urlWithQuery}`,
					);
				}

				let queryBoundary: string | undefined | null;
				let guardName: string | undefined;
				// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
				if (process.env.REACT_SSR) {
					// Which query boundary does this fetch belong to?
					queryBoundary = getQueryBoundary();

					// Which slow query guard does this fetch belong to?
					const storage = getAsyncLocalStorage(SLOW_QUERY_GUARD_STORAGE_NAME);
					guardName = storage?.getStore() as string | undefined;
				}

				// Record this request as in flight so that, if it never comes back, the data
				// fetcher timeout can still report it as the cause.
				const untrackInflightRequest = trackInflightRequest(queryBoundary, {
					method: finalOptions?.method || 'GET',
					url: urlWithQuery,
				});

				return fetchBase({
					name: 'fetchAgg',
					url: urlWithQuery,
					headers,
					options: finalOptions,
				})
					.finally(untrackInflightRequest)
					.then((result) => {
						if (guardName && window.__TIMED_OUT_SLOW_QUERY_GUARDS__?.has(guardName)) {
							// eslint-disable-next-line no-console
							console.info(
								`Returning data for timed out slowQueryGuard "${guardName}": ${
									finalOptions?.method || 'GET'
								} ${urlWithQuery}`,
							);
						}
						return result;
					});
			} else {
				// Non-bifrost server and client-side fetch
				const urlWithQuery = isForRelay ? finalUri : `${finalUri}?q=${operationNamesList}`;

				const headers = (finalOptions.headers || {}) as Record<string, string>;
				if (!headers['Atl-Edge-Region-Hint']) {
					// eslint-disable-next-line no-console
					console.warn(
						`Gateway GraphQL request missing Atl-Edge-Region-Hint header: ${urlWithQuery}`,
					);
				}

				return (
					// item might not always have a query property - as in the case of persisted queries
					(isExperienceAwareFetchAlwaysEnabled() ? experienceAwareFetch : cfetch)(
						urlWithQuery,
						finalOptions,
					)
				);
			}
		};

		let markingPrefix = 'wf/client-graphql/';
		// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
		if (process.env.REACT_SSR) {
			const queryBoundary = getQueryBoundary();
			const safeQueryBoundary = queryBoundary?.replace(/[^\-.\w]/g, '_');
			markingPrefix = safeQueryBoundary
				? `ssr-app/prepare/preloadQuery/dataFetcher:${safeQueryBoundary}/fetch:`
				: 'ssr-app/prepare/preloadQuery/fetch:';
		}
		const markingNames = operationNames.map((name) => {
			if (isForRelay) {
				name = `relay_${name}`;
			} else {
				name = `agg_${name}`;
			}
			return `${markingPrefix}${sanitizeMarkingName(name)}`;
		});
		// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
		if (process.env.REACT_SSR) {
			// Track individual GraphQL operations count for SSR
			SSRMeasures.operationsCountIncr(operationNames.length);

			// Wrap executeFetch to track GraphQL errors
			const errorTrackingExecuteFetch = async () => {
				try {
					return await executeFetch();
				} catch (error) {
					// Track GraphQL errors during SSR
					SSRMeasures.errorCountIncr(operationNames.length);
					throw error;
				}
			};

			return SSRMeasures.run(markingNames, errorTrackingExecuteFetch);
		}
		// Ignore batch queries because they are not deterministic. Don't make sense to show on performance waterfall.
		return operationNames.length > 1
			? executeFetch()
			: WaterfallMeasures.run(markingNames, executeFetch);
	};
};
